import React, { useEffect, useState, useRef } from "react";
import { Client } from 'paho-mqtt';
import PopupHeaderTabs from './PopupHeaderTabs';
import PopupBottomButtons from './PopupBottomButtons';
import BedroomLayout from './BedroomLayout';
import SensorConfigTable from './SensorConfigTable';
import RegionTabGroup from './RegionTabGroup';
import SensorSettings from './SensorSettings';
import LearningPeriodWidget from './LearningPeriodWidget';
import HtmlTableDocConfig42 from './HtmlTableDocConfig42';
import HeightLegend from './HeightLegend';
import TargetsList from './TargetsList';
import Log from './Log';
import Commenti from './Commenti';
import SensorWebSocket from './WebSocket';
import {fallingSensitivitySelect, sensorMountingSelect, ledPolicySelect, logLevelSelect, 
        algoProfileSelect, appLogSelect, productTypeSelect, rfRegulationZoneSelect, 
        rfBandWidthSelect, bleServerTypeSelect} from './utils/selects_config'
import {checkValue, checkSubregionValue, getMinMaxSensor45Ceiling, getMinMaxSensorWall, 
        generateGridLines45Ceiling, generateGridLinesWall} from './utils/regions_functions'
import {testFall, testSensitiveFall, testPresence, testBed, testType12, testType13} from './utils/test_functions'
import {formatToLocalISO} from './utils/general.js'
import sensor from "../APi/Sensor/sensor";
import dipasApi from '../APi/DiPas/dipasApi';
import LoggingApi from '../APi/Logging/LoggingApi';
import ceiling_mounting from '../imgs/45ceiling_mounting.svg';
import wall_mounting from '../imgs/wall_mounting.svg';


const defSelettore = "selettore selettoreFlex selettoreMiddle selettoreBorder padding-0-9rem";
const selSelettore = "selettore selettoreFlex selettoreMiddle selettorePurple padding-0-9rem selettoreShadow";

function getByPath(obj, path) {
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
}
function getFromProps(dbConfig, entry, default_value = 0) {
    const dbValue = getByPath(dbConfig, entry);

    if (dbValue === undefined) {
        return default_value;
    }

    const defaultType = typeof default_value;
    const dbType = typeof dbValue;

    if (dbType !== defaultType) {
        try {
            let converted;

            if (defaultType === "number") {
                converted = Number(dbValue);
                if (isNaN(converted)) throw new Error("Invalid number");
            } else if (defaultType === "boolean") {
                if (dbValue === "true" || dbValue === true) {
                    converted = true;
                } else if (dbValue === "false" || dbValue === false) {
                    converted = false;
                } else {
                    throw new Error("Invalid boolean");
                }
            } else if (defaultType === "string") {
                converted = String(dbValue);
            } else {
                throw new Error("Unsupported type");
            }

            console.log(`[Type Conversion] Converted "${entry}" from ${dbType} to ${defaultType}:`, converted);
            return converted;

        } catch (err) {
            console.warn(`[Type Mismatch] "${entry}" could not be converted from ${dbType} to ${defaultType}. Using default value:`, default_value);
            return default_value;
        }
    }

    return dbValue;
}

const SchedaSensore = (props) => {
    useEffect(() => {
        // console.log("SchedaSensore rendered", props);
    }, []);

    const propsConfig = props.sensore.deviceInfo.Config;
    const [logging, setLogging] = useState(props.logging);
    const [mod, setMod] = useState(0);
    const fullConfig = props.sensore.deviceInfo.Config.walabotConfig;

    const keysToExtract = [
        'xMax', 'xMin', 'yMax', 'yMin',
        'zMax', 'zMin', 'sensorHeight',
        'enterDuration', 'exitDuration',
    ];
    const initialRegion = keysToExtract.reduce((acc, key) => {
        acc[key] = fullConfig[key];
        return acc;
    }, {});
    const [Region, setRegion] = useState(initialRegion);
    const [lastValidRegion, setLastValidRegion] = useState(Region);

    const checkHorizontalPresence = (subregions) => {
        for (let i = 0; i < subregions.length; i++) {
            const item = subregions[i];

            if (!('isHorizontal' in item)) {
                item.isHorizontal = (i === 0 && item.isLowSnr === true) ? true : false;
            }

            const floatFields = ['xMin', 'xMax', 'yMin', 'yMax', 'zMin', 'zMax'];
            const intFields = ['enterDuration', 'exitDuration'];

            for (const field of [...floatFields, ...intFields]) {
                if (field in item && typeof item[field] === 'string') {
                    const parsed = floatFields.includes(field)
                        ? parseFloat(item[field])
                        : parseInt(item[field], 10);

                    if (!isNaN(parsed)) {
                        item[field] = parsed;
                    } else {
                        console.warn(`⚠️ Could not convert '${field}' to number in subregion ${i}:`, item[field]);
                    }
                }
            }
        }
        return subregions;
    }
    const dbSubregions = props.sensore.deviceInfo.Config.walabotConfig.trackerSubRegions;
    const correctedSubregions = checkHorizontalPresence(dbSubregions);
    const [Subregions, setSubregions] = useState(correctedSubregions);
    // const [lastValidSubregions, setLastValidSubregions] = useState(Subregions);
    const doorSubregions = Subregions
        .map((item, index) => ({ ...item, id: index }))
        .filter(item => item.isDoor)
        .map(({ name, id }) => ({ name, id }));

    const [selectedDoor, setSelectedDoor] = useState((doorSubregions.length > 0) ? doorSubregions[0].id : null);
    useEffect(() => {
        // console.log("Selected door changed:", selectedDoor);
    }, [selectedDoor]);

    const backupRef = useRef({ Region: null, Subregions: null });

    useEffect(() => {
        if (mod === 0 && backupRef.current.Region !== null) {
            console.log("🔁 Restoring from backup");
            // Restore deep copies to avoid shared references
            setRegion({ ...backupRef.current.Region });
            setSubregions([...backupRef.current.Subregions]);
        } else if (mod !== 0) {
            console.log("💾 Backing up region and subregions");
            // Store shallow copies (or deep if needed)
            backupRef.current = {
                Region: { ...Region },
                Subregions: [...Subregions],
            };
        }
    }, [mod]);
    
    const [status, setStatus] = useState(0);
    const [tabs, setTabs] = useState(0);

    const [logNData, setLogNData] = useState(0);
    const [logPage, setLogPage] = useState(1);
    const [falls, setFalls] = useState([]);


    /*** CONFIG DEVICE (appConfig) ***/
    const [silentMode, setSilentMode] = useState(getFromProps(propsConfig, "appConfig.silentMode", false)); // no buzzer
    const [ledPolicy, setLedPolicy] = useState(getFromProps(propsConfig, "appConfig.ledPolicy", 2)); // select
    const [volume, setVolume] = useState(getFromProps(propsConfig, "appConfig.volume", 0));
    const [logLevel, setLogLevel] = useState(getFromProps(propsConfig, "appConfig.logLevel", 2));
    const [learningModeEndTs, setLearningModeEndTs] = useState(getFromProps(propsConfig, "appConfig.learningModeEndTs", 0));
    const [learningModeStartTs, setLearningModeStartTs] = useState(getFromProps(propsConfig, "appConfig.learningModeStartTs", 0));
	const [enableTestMode, setEnableTestMode] = useState(getFromProps(propsConfig, "appConfig.enableTestMode", false));
    const [demoMode, setDemoMode] = useState(getFromProps(propsConfig, "appConfig.demoMode", false));
	const [offlineMode, setOfflineMode] = useState(getFromProps(propsConfig, "appConfig.offlineMode", true));
    const [appLogAutoLevel, setAppLogAutoLevel] = useState(getFromProps(propsConfig, "appConfig.appLogAutoLevel", 3)); // select
    const [appLogOnDemandLevel, setAppLogOnDemandLevel] = useState(getFromProps(propsConfig, "appConfig.appLogOnDemandLevel", 4)); // select
    const [legacyLogFileUpload, setLegacyLogFileUpload] = useState(getFromProps(propsConfig, "appConfig.legacyLogFileUpload", true));
    /*** CONFIG DEVICE (walabotConfig) ***/
    const [sensorMounting, setMounting] = useState(getFromProps(propsConfig, "walabotConfig.sensorMounting", 2)); // select
    const [algoProfile, setAlgoProfile] = useState(getFromProps(propsConfig, "walabotConfig.algoProfile", 1)); // select
    /*** CONFIG DEVICE (productType) ***/
    const [productType, setProductType] = useState(getFromProps(propsConfig, "productType", 0)); // select
    /*** CONFIG DEVICE (rfProfile) ***/
    const [rfRegulationZone, setRfRegulationZone] = useState(getFromProps(propsConfig, "rfProfile.rfRegulationZone", 0)); // select
    const [rfBandWidth, setRfBandWidth] = useState(getFromProps(propsConfig, "rfProfile.rfBandWidth", 0)); // select

    const SensoreSettings = {
        sensorMounting: [sensorMounting, setMounting],
        demoMode: [demoMode, setDemoMode],
        enableTestMode: [enableTestMode, setEnableTestMode],
        offlineMode: [offlineMode, setOfflineMode],
        silentMode: [silentMode, setSilentMode],
        ledPolicy: [ledPolicy, setLedPolicy],
        volume: [volume, setVolume],
        logLevel: [logLevel, setLogLevel]
    };
    const SensoreSettingsConfig = {
        switch: ['demoMode', 'enableTestMode', 'silentMode', 'offlineMode'],
        select: ['sensorMounting', 'ledPolicy', 'logLevel'],
        selectOptions: {
            sensorMounting: sensorMountingSelect, 
            ledPolicy: ledPolicySelect, 
            logLevel: logLevelSelect
        }
    };

    const SensoreExtraSettings = {
        algoProfile: [algoProfile, setAlgoProfile],
        appLogAutoLevel: [appLogAutoLevel, setAppLogAutoLevel],
        appLogOnDemandLevel: [appLogOnDemandLevel, setAppLogOnDemandLevel],
        legacyLogFileUpload: [legacyLogFileUpload, setLegacyLogFileUpload],
        productType: [productType, setProductType],
        rfRegulationZone: [rfRegulationZone, setRfRegulationZone],
        rfBandWidth: [rfBandWidth, setRfBandWidth]
    };
    const SensoreExtraSettingsConfig = {
        switch: ['legacyLogFileUpload'],
        select: ['algoProfile', 'appLogAutoLevel', 'appLogOnDemandLevel', 'productType', 'rfRegulationZone','rfBandWidth'],
        selectOptions: {
            algoProfile: algoProfileSelect, 
            appLogAutoLevel: appLogSelect,
            appLogOnDemandLevel: appLogSelect, 
            productType: productTypeSelect,
            rfRegulationZone: rfRegulationZoneSelect, 
            rfBandWidth: rfBandWidthSelect
        }
    };



    /*** CONFIG CADUTE (appConfig) ***/
    const [confirmedToAlertTimeoutSec, setConfirmedToAlertTimeoutSec] = useState(getFromProps(propsConfig, "appConfig.confirmedToAlertTimeoutSec", 40));
	const [callingDurationSec, setCallingDurationSec] = useState(getFromProps(propsConfig, "appConfig.callingDurationSec", 30));
	const [enableSensitiveMode, setEnableSensitiveMode] = useState(getFromProps(propsConfig, "appConfig.enableSensitiveMode", false));
	const [sensitivityLevel, setSensitivityLevel] = useState(getFromProps(propsConfig, "appConfig.sensitivityLevel", 0.7));
	const [thMinEventsForFirstDecision, setThMinEventsForFirstDecision] = useState(getFromProps(propsConfig, "appConfig.thMinEventsForFirstDecision", 5));
    const [thNumOfDetectionsInChain, setThNumOfDetectionsInChain] = useState(getFromProps(propsConfig, "appConfig.thNumOfDetectionsInChain", 4));
    /*** CONFIG CADUTE (walabotConfig) ***/
    const [fallingMitigatorEnabled, setFallingMitigatorEnabled] = useState(getFromProps(propsConfig, "walabotConfig.fallingMitigatorEnabled", true));
    const [fallingMitigatorThreshold, setFallingMitigatorThreshold] = useState(getFromProps(propsConfig, "walabotConfig.fallingMitigatorThreshold", 0)); // ???
    const [fallingSensitivity, setFallingSensitivity] = useState(getFromProps(propsConfig, "walabotConfig.fallingSensitivity", 1)); // select
	const [durationUntilConfirm_sec, setDurationUntilConfirm_sec] = useState(getFromProps(propsConfig, "walabotConfig.durationUntilConfirm_sec", 52));
    const [minTimeOfTarInFallLoc_sec, setMinTimeOfTarInFallLoc_sec] = useState(getFromProps(propsConfig, "walabotConfig.minTimeOfTarInFallLoc_sec", 30));

    const CaduteSettings = {
        confirmedToAlertTimeoutSec: [confirmedToAlertTimeoutSec, setConfirmedToAlertTimeoutSec],
        callingDurationSec: [callingDurationSec, setCallingDurationSec],
        durationUntilConfirm_sec: [durationUntilConfirm_sec, setDurationUntilConfirm_sec],
        minTimeOfTarInFallLoc_sec: [minTimeOfTarInFallLoc_sec, setMinTimeOfTarInFallLoc_sec]
    };
    const CaduteSettingsConfig = {
    };

    const CaduteT5Settings = {
        fallingSensitivity: [fallingSensitivity, setFallingSensitivity],
        fallingMitigatorEnabled: [fallingMitigatorEnabled, setFallingMitigatorEnabled],
        fallingMitigatorThreshold: [fallingMitigatorThreshold, setFallingMitigatorThreshold]
    };
    const CaduteT5SettingsConfig = {
        switch: ['fallingMitigatorEnabled'],
        select: ['fallingSensitivity'],
        selectOptions: {
            fallingSensitivity: fallingSensitivitySelect
        }
    };

    const CaduteT8Settings = {
        enableSensitiveMode: [enableSensitiveMode, setEnableSensitiveMode],
        sensitivityLevel: [sensitivityLevel, setSensitivityLevel],
        thMinEventsForFirstDecision: [thMinEventsForFirstDecision, setThMinEventsForFirstDecision],
        thNumOfDetectionsInChain: [thNumOfDetectionsInChain, setThNumOfDetectionsInChain]
    };
    const CaduteT8SettingsConfig = {
        switch: ['enableSensitiveMode']
    };



    /*** CONFIG PRESENZA (appConfig) ***/
    const [presenceReportMinRateMills, setPresenceReportMinRateMills] = useState(getFromProps(propsConfig, "appConfig.presenceReportMinRateMills", 60000));
    const [enablePresencePeriodicReport, setEnablePresencePeriodicReport] = useState(getFromProps(propsConfig, "appConfig.enablePresencePeriodicReport", true));
	const [enableDoorEvents, setEnableDoorEvents] = useState(getFromProps(propsConfig, "appConfig.enableDoorEvents", false));
    const [enableOutOfBed, setEnableOutOfBed] = useState(getFromProps(propsConfig, "appConfig.enableOutOfBed", false));
    const [enableAnalytics, setEnableAnalytics] = useState(getFromProps(propsConfig, "appConfig.enableAnalytics", true));
	const [suspendDuration_sec, setSuspendDuration_sec] = useState(getFromProps(propsConfig, "appConfig.suspendDuration_sec", 900));
	const [enableTelemetriesOnEventDuringSuspend, setEnableTelemetriesOnEventDuringSuspend] = useState(getFromProps(propsConfig, "appConfig.enableTelemetriesOnEventDuringSuspend", true));
    /*** CONFIG PRESENZA (walabotConfig) ***/
    const [bedExitNFramesToReset, setBedExitNFramesToReset] = useState(getFromProps(propsConfig, "walabotConfig.bedExitNFramesToReset", 100));
    const [expandRegInMeters, setExpandRegInMeters ] = useState(getFromProps(propsConfig, "walabotConfig.expandRegInMeters", 0.3));
    const [zThresholdMeters, setZThresholdMeters] = useState(getFromProps(propsConfig, "walabotConfig.zThresholdMeters", 0.7));

    const PresenceEventsSettings = {
        presenceReportMinRateMills: [presenceReportMinRateMills, setPresenceReportMinRateMills],
        enablePresencePeriodicReport: [enablePresencePeriodicReport, setEnablePresencePeriodicReport],
        enableDoorEvents: [enableDoorEvents, setEnableDoorEvents],
        enableOutOfBed: [enableOutOfBed, setEnableOutOfBed],
        enableAnalytics: [enableAnalytics, setEnableAnalytics],
        suspendDuration_sec: [suspendDuration_sec, setSuspendDuration_sec],
        enableTelemetriesOnEventDuringSuspend: [enableTelemetriesOnEventDuringSuspend, setEnableTelemetriesOnEventDuringSuspend],
        // bedExitNFramesToReset: [bedExitNFramesToReset, setBedExitNFramesToReset]
        expandRegInMeters: [expandRegInMeters, setExpandRegInMeters],
        zThresholdMeters: [zThresholdMeters, setZThresholdMeters]
    };
    const PresenceEventsSettingsConfig = {
        switch: ['enablePresencePeriodicReport','enableDoorEvents','enableOutOfBed', 'enableAnalytics', 'enableTelemetriesOnEventDuringSuspend']
    };



    /*** CONFIG TELEMETRY (appConfig) ***/
    const [telemAlwaysOn, setTelemAlwaysOn] = useState(getFromProps(propsConfig, "appConfig.telemAlwaysOn", false));
    const [telemOnBedExit, setTelemOnBedExit] = useState(getFromProps(propsConfig, "appConfig.telemOnBedExit", false));
    const [telemOnFall, setTelemOnFall] = useState(getFromProps(propsConfig, "appConfig.telemOnFall", false));
    const [telemOnSensitiveFall, setTelemOnSensitiveFall] = useState(getFromProps(propsConfig, "appConfig.telemOnSensitiveFall", false));
    const [telemOnDoorEvent, setTelemOnDoorEvent] = useState(getFromProps(propsConfig, "appConfig.telemOnDoorEvent", false));
    const [telemOnOutOfBed, setTelemOnOutOfBed] = useState(getFromProps(propsConfig, "appConfig.telemOnOutOfBed", false));
    /*** CONFIG TELEMETRY (walabotConfig) ***/
    const [enableBedExitTelemetry, setEnableBedExitTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableBedExitTelemetry", false));
	const [enableBedExitStateTelemetry, setEnableBedExitStateTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableBedExitStateTelemetry", false));
    const [enableTrackerTargetTelemetry, setEnableTrackerTargetTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableTrackerTargetTelemetry", false));
    const [enableDoorEventTelemetry, setEnableDoorEventTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableDoorEventTelemetry", false));
    const [enablePeakTelemetry, setEnablePeakTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enablePeakTelemetry", false));
    const [enableAboveThPointTelemetry, setEnableAboveThPointTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableAboveThPointTelemetry", false));
    const [enableIslandPointTelemetry, setEnableIslandPointTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableIslandPointTelemetry", false));
    const [enableHeightProfileTelemetry, setEnableHeightProfileTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableHeightProfileTelemetry", false));
    const [enableOtfPointTelemetry, setEnableOtfPointTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableOtfPointTelemetry", false));
    const [enableFallingTelemetry, setEnableFallingTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableFallingTelemetry", false));
    const [enableSensitiveFallingTelemetry, setEnableSensitiveFallingTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableSensitiveFallingTelemetry", false));
    const [enablePresenceTelemetry, setEnablePresenceTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enablePresenceTelemetry", false));
    const [enableImageParamsTelemetry, setEnableImageParamsTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableImageParamsTelemetry", false));
    const [enableInterfererLocHistoryTelemetry, setEnableInterfererLocHistoryTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableInterfererLocHistoryTelemetry", false));
    const [enableMtiParamsTelemetry, setEnableMtiParamsTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableMtiParamsTelemetry", false));
    const [enableReferenceTelemetry, setEnableReferenceTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableReferenceTelemetry", false));
    const [enableSuiteTelemetry, setEnableSuiteTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableSuiteTelemetry", false));
    const [enableClustersTelemetry, setEnableClustersTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableClustersTelemetry", false));
    const [enableSubRegionStateTelemetry, setEnableSubRegionStateTelemetry] = useState(getFromProps(propsConfig, "walabotConfig.enableSubRegionStateTelemetry", false));
    
    const TelemetrySettings = {
        telemAlwaysOn: [telemAlwaysOn, setTelemAlwaysOn],
        telemOnBedExit: [telemOnBedExit, setTelemOnBedExit],
        telemOnFall: [telemOnFall, setTelemOnFall],
        telemOnSensitiveFall: [telemOnSensitiveFall, setTelemOnSensitiveFall],
        telemOnDoorEvent: [telemOnDoorEvent, setTelemOnDoorEvent],
        telemOnOutOfBed: [telemOnOutOfBed, setTelemOnOutOfBed]
    };
    const TelemetrySettingsConfig = {
        switch: ['telemAlwaysOn', 'telemOnBedExit', 'telemOnFall', 'telemOnSensitiveFall', 'telemOnDoorEvent', 'telemOnOutOfBed']
    };

    const TelemetryVayyarSettings = {
        enableBedExitTelemetry: [enableBedExitTelemetry, setEnableBedExitTelemetry],
	    enableBedExitStateTelemetry: [enableBedExitStateTelemetry, setEnableBedExitStateTelemetry],
        enableTrackerTargetTelemetry: [enableTrackerTargetTelemetry, setEnableTrackerTargetTelemetry],
        enableDoorEventTelemetry: [enableDoorEventTelemetry, setEnableDoorEventTelemetry],
        enablePeakTelemetry: [enablePeakTelemetry, setEnablePeakTelemetry],
        enableAboveThPointTelemetry: [enableAboveThPointTelemetry, setEnableAboveThPointTelemetry],
        enableIslandPointTelemetry: [enableIslandPointTelemetry, setEnableIslandPointTelemetry],
        enableHeightProfileTelemetry: [enableHeightProfileTelemetry, setEnableHeightProfileTelemetry],
        enableOtfPointTelemetry: [enableOtfPointTelemetry, setEnableOtfPointTelemetry],
        enableFallingTelemetry: [enableFallingTelemetry, setEnableFallingTelemetry],
        enableSensitiveFallingTelemetry: [enableSensitiveFallingTelemetry, setEnableSensitiveFallingTelemetry],
        enablePresenceTelemetry: [enablePresenceTelemetry, setEnablePresenceTelemetry],
        enableImageParamsTelemetry: [enableImageParamsTelemetry, setEnableImageParamsTelemetry],
        enableInterfererLocHistoryTelemetry: [enableInterfererLocHistoryTelemetry, setEnableInterfererLocHistoryTelemetry],
        enableMtiParamsTelemetry: [enableMtiParamsTelemetry, setEnableMtiParamsTelemetry],
        enableReferenceTelemetry: [enableReferenceTelemetry, setEnableReferenceTelemetry],
        enableSuiteTelemetry: [enableSuiteTelemetry, setEnableSuiteTelemetry],
        enableClustersTelemetry: [enableClustersTelemetry, setEnableClustersTelemetry],
        enableSubRegionStateTelemetry: [enableSubRegionStateTelemetry, setEnableSubRegionStateTelemetry]
    };
    const TelemetryVayyarSettingsConfig = {
        switch: [
            'enableBedExitTelemetry', 'enableBedExitStateTelemetry', 'enableTrackerTargetTelemetry', 'enableDoorEventTelemetry',
            'enablePeakTelemetry', 'enableAboveThPointTelemetry', 'enableIslandPointTelemetry', 'enableHeightProfileTelemetry',
            'enableOtfPointTelemetry', 'enableFallingTelemetry', 'enableSensitiveFallingTelemetry', 'enablePresenceTelemetry',
            'enableImageParamsTelemetry', 'enableInterfererLocHistoryTelemetry', 'enableMtiParamsTelemetry', 'enableReferenceTelemetry',
            'enableSuiteTelemetry', 'enableClustersTelemetry', 'enableSubRegionStateTelemetry'
        ]
    };



    /*** CONFIG ALTRO (appConfig) ***/
    const [dspRecordsPublishPolicy, setDspRecordsPublishPolicy] = useState(getFromProps(propsConfig, "appConfig.dspRecordsPublishPolicy", false));
    const [dspRecordsPublishMaxLatency_sec, setDspRecordsPublishMaxLatency_sec] = useState(getFromProps(propsConfig, "appConfig.dspRecordsPublishMaxLatency_sec", 10));
	const [enableBeaconScanner, setEnableBeaconScanner] = useState(getFromProps(propsConfig, "appConfig.enableBeaconScanner", false));
	// const [bleBeaconMacs, setBleBeaconMacs] = [];
	const [bleBeaconRssiThreshold, setBleBeaconRssiThreshold] = useState(getFromProps(propsConfig, "appConfig.bleBeaconRssiThreshold", -80));
    const [bleServerType, setBleServerType] = useState(getFromProps(propsConfig, "appConfig.bleServerType", 1)); // select
    const [bleCustomDeviceName, setBleCustomDeviceName] = useState(getFromProps(propsConfig, "appConfig.bleCustomDeviceName", "VC000"));
	const [enableRssiMonitor, setEnableRssiMonitor] = useState(getFromProps(propsConfig, "appConfig.enableRssiMonitor", true));
	const [rssiThresholdRssiMonitor, setRssiThresholdRssiMonitor] = useState(getFromProps(propsConfig, "appConfig.rssiThresholdRssiMonitor", -70));
	const [samplesNumRssiMonitor, setSamplesNumRssiMonitor] = useState(getFromProps(propsConfig, "appConfig.samplesNumRssiMonitor", 30));
    const [enableWifiHealthMonitor, setEnableWifiHealthMonitor] = useState(getFromProps(propsConfig, "appConfig.enableWifiHealthMonitor", true));
    const [maxDisconnetionDurationSecWifiHealthMonitor, setMaxDisconnetionDurationSecWifiHealthMonitor] = useState(getFromProps(propsConfig, "appConfig.maxDisconnetionDurationSecWifiHealthMonitor", 240));
    const [disconnectionsBurstLimitWifiHealthMonitor, setDisconnectionsBurstLimitWifiHealthMonitor] = useState(getFromProps(propsConfig, "appConfig.disconnectionsBurstLimitWifiHealthMonitor", 15));
    const [maxDisconnectionsPerHourAverageWifiHealthMonitor, setMaxDisconnectionsPerHourAverageWifiHealthMonitor] = useState(getFromProps(propsConfig, "appConfig.maxDisconnectionsPerHourAverageWifiHealthMonitor", 15));
    const [ntpPrimaryBackupServer, setNtpPrimaryBackupServer] = useState(getFromProps(propsConfig, "appConfig.ntpPrimaryBackupServer", "europe.pool.ntp.org"));
    const [ntpSecondaryBackupServer, setNtpSecondaryBackupServer] = useState(getFromProps(propsConfig, "appConfig.ntpSecondaryBackupServer", "us.pool.ntp.org"));
	const [dryContactActivationDuration_sec, setDryContactActivationDuration_sec] = useState(getFromProps(propsConfig, "walabotConfig.dryContactActivationDuration_sec", 30));
    /*** CONFIG ALTRO (walabotConfig) ***/
    const [performAgc, setPerformAgc] = useState(getFromProps(propsConfig, "walabotConfig.performAgc", true));
    const [performHeatup, setPerformHeatup] = useState(getFromProps(propsConfig, "walabotConfig.performHeatup", true));

    const BLESettings = {
        enableBeaconScanner: [enableBeaconScanner, setEnableBeaconScanner],
        bleBeaconRssiThreshold: [bleBeaconRssiThreshold, setBleBeaconRssiThreshold],
        bleServerType: [bleServerType, setBleServerType],
        bleCustomDeviceName: [bleCustomDeviceName, setBleCustomDeviceName]
    };
    const BLESettingsConfig = {
        switch: ['enableBeaconScanner'],
        text: ['bleCustomDeviceName'],
        select: ['bleServerType'],
        selectOptions: {
            bleServerType: bleServerTypeSelect
        }
    };

    const WiFiSettings = {
        enableRssiMonitor: [enableRssiMonitor, setEnableRssiMonitor],
        rssiThresholdRssiMonitor: [rssiThresholdRssiMonitor, setRssiThresholdRssiMonitor],
        samplesNumRssiMonitor: [samplesNumRssiMonitor, setSamplesNumRssiMonitor],
        enableWifiHealthMonitor: [enableWifiHealthMonitor, setEnableWifiHealthMonitor],
        maxDisconnetionDurationSecWifiHealthMonitor: [maxDisconnetionDurationSecWifiHealthMonitor, setMaxDisconnetionDurationSecWifiHealthMonitor],
        disconnectionsBurstLimitWifiHealthMonitor: [disconnectionsBurstLimitWifiHealthMonitor, setDisconnectionsBurstLimitWifiHealthMonitor],
        maxDisconnectionsPerHourAverageWifiHealthMonitor: [maxDisconnectionsPerHourAverageWifiHealthMonitor, setMaxDisconnectionsPerHourAverageWifiHealthMonitor]
    };
    const WiFiSettingsConfig = {
        switch: ['enableRssiMonitor', 'enableWifiHealthMonitor'],
    };

    const OtherSettings = {
        dspRecordsPublishPolicy: [dspRecordsPublishPolicy, setDspRecordsPublishPolicy],
        dspRecordsPublishMaxLatency_sec: [dspRecordsPublishMaxLatency_sec, setDspRecordsPublishMaxLatency_sec],
        ntpPrimaryBackupServer: [ntpPrimaryBackupServer, setNtpPrimaryBackupServer],
        ntpSecondaryBackupServer: [ntpSecondaryBackupServer, setNtpSecondaryBackupServer],
        dryContactActivationDuration_sec: [dryContactActivationDuration_sec, setDryContactActivationDuration_sec],
        performAgc: [performAgc, setPerformAgc],
        performHeatup: [performHeatup, setPerformHeatup]
    };
    const OtherSettingsConfig = {
        switch: ['dspRecordsPublishPolicy', 'performAgc', 'performHeatup'],
        text: ['ntpPrimaryBackupServer', 'ntpSecondaryBackupServer'],
    };



    /*** CONFIG Deprecated or Not Used */
    /* appConfig */
    const [ledmode, setLedmode] = useState(getFromProps(propsConfig, "appConfig.ledMode"));
    const [telemetryPolicy, setTelemetryPolicy] = useState(getFromProps(propsConfig, "appConfig.telemetryPolicy"));
    const [telemetryTransport, setTelemetryTransport] = useState(getFromProps(propsConfig, "appConfig.telemetryTransport"));
    // "dryContacts": {"primary": {"mode": 0,"policy": 0},"secondary": {"mode": 0,"policy": 0}},
    const [trackerTargetsDebugPolicy, setTrackerTargetsDebugPolicy] = useState(getFromProps(propsConfig, "appConfig.trackerTargetsDebugPolicy"));
    // "smartReboot"
    /* walabotConfig */
    // const [maxTargetsForFallingTrigger, setMaxTargetsForFallingTrigger] = useState(getFromProps(propsConfig, "walabotConfig.maxTargetsForFallingTrigger"));
    const [bedExitEnabled, setBedExitEnabled] = useState(getFromProps(propsConfig, "walabotConfig.bedExitEnabled"));
    const [bedExitPredictionThreshold, setBedExitPredictionThreshold] = useState(getFromProps(propsConfig, "walabotConfig.bedExitPredictionThreshold"));
    // "dryContactActivationDuration_sec": c'è in appConfig
    

    
    /*** CONFIG Struttura ***/
    
    const TipoStanza = [
    { value: "none", id: "Seleziona Tipo" },
    { value: "Bedroom", id: "Camera da letto" },
    { value: "Bathroom", id: "Bagno" },
    { value: "Diningroom", id: "Soggiorno" },
    { value: "Kitchen", id: "Cucina" },
    { value: "Other", id: "Altro" },
    { value: "Passage", id: "Varco" }
    ]
    
    const [stanza, setStanza] = useState(props.sensore.deviceInfo.Stanza);
    const [tipoStanza, setTipoStanza] = useState(props.sensore.deviceInfo.Tipo);
    const [piano, setPiano] = useState(props.sensore.deviceInfo.Piano);
    const [struttura, setStruttura] = useState(props.sensore.deviceInfo.Struttura);
    const [ListaStrutture, setListaStrutture] = useState([]);
    const [ListaPiani, setListaPiani] = useState([]);

    const CONFIGURATOR_VERSION = 1.0;
    const configVersion = props.sensore.deviceInfo.ConfigVersion;
    const isConfigVersionOk = !((configVersion === undefined) || (configVersion < CONFIGURATOR_VERSION));
    
    const configError = props.sensore.deviceInfo.configError;
    const isConfigErrorAvailable = (configError !== undefined);
    const isConfigErrorOK = isConfigErrorAvailable ? !configError : false;

    const configFeedback = props.sensore.deviceInfo.configFeedback;
    const isConfigFeedbackAvailable = (configFeedback !== undefined);
    const isConfigFeedbackOK = isConfigFeedbackAvailable ? configFeedback : false;
    
    const configSanity = props.sensore.deviceInfo.ConfigSanity;
    const isConfigSanityAvailable = (configSanity !== undefined);
    const isConfigSanityOK = isConfigSanityAvailable ? (configSanity == 0) : false;

    const configDate = props.sensore.deviceInfo.ConfigDate;
    const configDateISO = (configDate === undefined)
        ? '-'
        : formatToLocalISO(configDate);
    const configFeedbackDate = props.sensore.deviceInfo.configFeedbackDate;
    const configFeedbackDateISO = (configFeedbackDate === undefined)
        ? '-'
        : formatToLocalISO(configFeedbackDate);
    const configFeedbackReceived = (configFeedbackDate === undefined) ? false : (configFeedbackDate > configDate);

    const [alertInput, setAlertInput] = useState(0);
    
    const [mqttclient, setMqttclient] = useState();

    const token = props.token;

    async function mqttDisconnect() {
        mqttclient.disconnect();
    }

    async function getlistaPiani(ListaStrutture) {
        try {
            for (let i = 0; i < ListaStrutture.length; i++) {
                if (ListaStrutture[i].name === struttura) {
                    await dipasApi.getPiani(token, ListaStrutture[i].id).then((piani) => {
                        const arrayPiani = [];
                        for (let i = 0; i < piani.length; i++) {
                            arrayPiani.push({ nome: piani[i].descrizione, id: piani[i].id });
                        }
                        setListaPiani(arrayPiani);
                    });
                }
            }
        } catch (error) {
            console.log(error);
        }
    }

    async function getlistaStrutture() {
        try {
            await dipasApi.getStrutture(token).then((strutture) => {
                setListaStrutture(strutture);
                getlistaPiani(strutture);
            });
        } catch (error) {
            console.log(error);
        }
    }

    async function getLoggingDataSize() {
        try {
            const data = await LoggingApi.getLoggingDataSize(token, props.sensore.deviceInfo.deviceId);
            return data; 
        } catch (error) {
            console.log(error);
        }
    }

    async function getLoggingDataRange(from, to) {
        try {
            const data = await LoggingApi.getLoggingDataRange(token, props.sensore.deviceInfo.deviceId, from, to);
            // console.log(data)
            return data;
        } catch (error) {
            console.log(error);
        }
    }

    async function eraseLoggingData() {
        try {
            const data = await LoggingApi.eraseLogging(token, props.sensore.deviceInfo.deviceId);
            return data;
        } catch (error) {
            console.log(error);
        }
    }

    async function eraseLogging() {
        await eraseLoggingData();
        const n = await getLoggingDataSize();
        setLogNData(n);
        setLogPage(1);
    }

    async function subscribeTotargetTopic() {
        let client = await new Client(process.env.REACT_APP_MQTT_URL, parseInt(process.env.REACT_APP_MQTT_PORT), "web_" + parseInt(Math.random() * 100, 10));
        let boolSSL = (/true/).test(process.env.REACT_APP_MQTT_SSL);
        await client.connect({
            password: process.env.REACT_APP_MQTT_PASSWORD,
            userName: process.env.REACT_APP_MQTT_USER,
            useSSL: boolSSL,
            onSuccess: () => {
                // Now that we are connected, subscribe to topics here
                client.subscribe("/devices/"+props.sensore.deviceInfo.deviceId+"/events", {
                    onSuccess: () => {
                        console.log("subscribed!!!")
                        client.onMessageArrived = (msg) => {
                            try {
                                const data = JSON.parse(msg.payloadString);

                                if (data.type === 4) {
                                    const receivedAt = new Date();
                                    const hours = receivedAt.getHours().toString().padStart(2, '0');
                                    const minutes = receivedAt.getMinutes().toString().padStart(2, '0');
                                    const seconds = receivedAt.getSeconds().toString().padStart(2, '0');
                                    const timeString = `${hours}:${minutes}:${seconds}`;
                                    setLatestType4(timeString);

                                    // presence
                                    const presenceDetected = data.payload.presenceDetected;
                                    setPresence(presenceDetected);

                                    // presence subregions
                                    const presenceRegionMap = data.payload.presenceRegionMap;
                                    const booleans = presenceRegionMap
                                        ? Object.keys(presenceRegionMap)
                                            .sort((a, b) => Number(a) - Number(b)) // Ensure keys are in order: "0", "1", "2", ...
                                            .map((key) => Boolean(presenceRegionMap[key]))
                                        : [];
                                    setRegionBooleans(booleans);

                                    // targets
                                    const targets = data.payload?.trackerTargets;
                                    if (Array.isArray(targets)) {
                                        const formatted = targets
                                            .filter(t => t && typeof t.xPosCm === 'number')  // Filter out null/undefined/invalid
                                            .map((target) => ({
                                                xPosCm: target.xPosCm,
                                                yPosCm: target.yPosCm,
                                                zPosCm: target.zPosCm,
                                                id: target.id,
                                            }));

                                        setReceivedTargets(formatted);
                                        //console.log("✅ New targets added:", formatted);
                                    } else {
                                        // Type 4 but no targets — clear the list
                                        setReceivedTargets([]);
                                        //console.log("⚠️ Type 4 message received with no trackerTargets. Cleared target list.");
                                    }
                                } else {
                                    // Ignore non-type-4 messages
                                    //console.log("ℹ️ Ignored message: type !== 4");
                                }
                            } catch (err) {
                                console.error("❌ Failed to parse MQTT message:", err);
                            }
                        };
                    },
                    onFailure: (e) => {
                        console.log("Subscription failed", e);
                    }
                });
            },
            onFailure: (e) => {
                console.log("connect failed", e);
            }
        });
        setMqttclient(client);
    }

    useEffect(() => {
        (async () => {
            await getlistaStrutture();
            const n = await getLoggingDataSize();
            setLogNData(n);
            await setData();
            await subscribeTotargetTopic();
        }
        )();
    }, []);


    async function setData() {
        setStanza(props.sensore.deviceInfo.Stanza)
        setPiano(props.sensore.deviceInfo.Piano)
        setTipoStanza(props.sensore.deviceInfo.Tipo)
        setStruttura(props.sensore.deviceInfo.Struttura)
    }

    function tabConfig(id) { // Apre e chiude le tab della struttura
        let tab = document.getElementById(id);
        let arrow = document.getElementById('arrow' + id);
        if (tab.style.maxHeight === "2.6em") {
            tab.style.maxHeight = "100em";
            arrow.style.transform = "rotate(180deg)";
        }
        else {
            tab.style.maxHeight = "2.6em";
            arrow.style.transform = "rotate(0deg)";
        }
    }

    const setMainRegion = async (value, object) => {
        const parsedValue = parseFloat(value);
        const tempRegion = { ...Region, [object]: parsedValue };

        let alertMask = false;
        for (const [key, val] of Object.entries(tempRegion)) {
            const result = checkValue(key, val, Region, sensorMounting);
            if (result) {
                alertMask = true;
                break;
            }
        }

        setRegion(tempRegion);
        if (!alertMask) {
            setLastValidRegion(tempRegion);
        }
        setAlertInput(alertMask);
    }

    const setSubregion = async (value, index, object) => {
        const nonCheckObjectx = ["isFallingDetection", "isPresenceDetection", "isLowSnr", "isHorizontal", "isDoor", "enterDuration", "exitDuration"];
        if (nonCheckObjectx.includes(object)) {
            const newRegions = [...Subregions];
            newRegions[index][object] = value;
            setSubregions(newRegions);
            return;
        } else {
            const parsedValue = parseFloat(value);
            const updatedSubregions = [...Subregions];
            updatedSubregions[index] = {
                ...updatedSubregions[index],
                [object]: parsedValue
            };

            let alertMask = false;
            for (const [ind, subregion] of Object.entries(updatedSubregions)) {
                for (const [key, val] of Object.entries(subregion)) {
                    const result = checkSubregionValue(key, subregion, Region);
                    if (result) {
                        alertMask = true;
                        break;
                    }
                }
            }

            setSubregions(updatedSubregions);
            if (!alertMask) {
                // setLastValidSubregions(updatedSubregions);
            }
            setAlertInput(alertMask);
        }
    }

    const setSubregionName = (value, index) => {
        const newRegions = [...Subregions];
        newRegions[index].name = value;
        setSubregions(newRegions);
    }

    const renderInputXYZWithError = (measurement1, label1, value1, measurement2, label2, value2) => {
        const isError1 = checkValue(measurement1, value1, Region, sensorMounting);
        const isError2 = checkValue(measurement2, value2, Region, sensorMounting);
        return (
            <tr>
                <td colSpan={1}>{label1}</td>
                <td colSpan={1}>
                    <input
                        style={isError1 ? { border: ".7px solid rgb(207, 19, 19)" } : null}
                        type="number"
                        name={label1}
                        id={measurement1}
                        value={value1}
                        disabled={mod === 0}
                        onChange={(e) => setMainRegion(e.target.value, measurement1)}
                    />
                    {isError1 && <div className="errorBox">{isError1}</div>}
                </td>
                <td colSpan={1}>{label2}</td>
                <td colSpan={1}>
                    <input
                        style={isError2 ? { border: ".7px solid rgb(207, 19, 19)" } : null}
                        type="number"
                        name={label2}
                        id={measurement2}
                        value={value2}
                        disabled={mod === 0}
                        onChange={(e) => setMainRegion(e.target.value, measurement2)}
                    />
                    {isError2 && <div className="errorBox">{isError2}</div>}
                </td>
            </tr>
        );
    }

    const renderInputWithError = (measurement, label, value) => {
        const isError = checkValue(measurement, value, Region, sensorMounting);
        return (
            <tr>
                <td colSpan={2}>{label}</td>
                <td colSpan={2}>
                    <input
                        style={isError ? { border: ".7px solid rgb(207, 19, 19)" } : null}
                        type="number"
                        name={label}
                        id={measurement}
                        value={value}
                        disabled={mod === 0}
                        onChange={(e) => setMainRegion(e.target.value, measurement)}
                    />
                    {isError && <div className="errorBox">{isError}</div>}
                </td>
            </tr>
        );
    }

    const renderSubregionInputWithError = (label, minKey, maxKey, subregion, index) => {
        const isMinError = checkSubregionValue(minKey, subregion, Region);
        const isMaxError = checkSubregionValue(maxKey, subregion, Region);

        return (
            <tr key={`${minKey}-${maxKey}`}>
                <td width="10%">{label}</td>
                <td width="10%">min:</td>
                <td width="35%">
                    <input
                        style={isMinError ? { border: ".7px solid rgb(207, 19, 19)" } : undefined}
                        type="number"
                        name={minKey}
                        value={subregion[minKey]}
                        disabled={mod === 0}
                        onChange={(e) => setSubregion(e.target.value, index, minKey)}
                    />
                    {isMinError && <div className="errorBox">{isMinError}</div>}
                </td>
                <td width="10%">max:</td>
                <td width="35%">
                    <input
                        style={isMaxError ? { border: ".7px solid rgb(207, 19, 19)" } : undefined}
                        type="number"
                        name={maxKey}
                        value={subregion[maxKey]}
                        disabled={mod === 0}
                        onChange={(e) => setSubregion(e.target.value, index, maxKey)}
                    />
                    {isMaxError && <div className="errorBox">{isMaxError}</div>}
                </td>
            </tr>
        );
    }

    const addSubregion = (value) => {
        var newRegions = [];
        newRegions = [...Subregions];
        if(newRegions.length == 6) return;

        const sel = value.selected;
        const isLetto = (sel.bed == true);
        const isPorta = (sel.door == true);
        newRegions.push(
            {
                "xMin": 0,
                "xMax": sel.width,
                "yMin": 0,
                "yMax": sel.height,
                "zMin": 0,
                "zMax": 1.80,
                "enterDuration": 10,
                "exitDuration": 10,
                "isFallingDetection": isLetto ? false : true,
                "isPresenceDetection": true,
                "isLowSnr": isLetto ? true : false,
                "isHorizontal": isLetto ? true : false,
                "isDoor": isPorta ? true : false,
                "name": value.label
            }
        );
        setSubregions(newRegions);
    }

    const removeSubregion = (index) => {
        const newRegions = [...Subregions];
        newRegions.splice(index, 1);
        setSubregions(newRegions);
    }

    const sanityMessages = [
        "- out-of-bed ma non c'è un letto",
        "- c'è un letto in più",
        "- il letto non è nella sottoregione 0",
        "- enableDoor event non attivo",
        "- enableDoor event attivo ma non ci sono porte"
    ];

    function sanityCheck() {
        let isThereDoor = false;
        let isThereBed = false;
        let isThereWrongBed = false;
        for (let i in Subregions) {
            const subregion = Subregions[i];
            if ((i === "0") && (subregion.isLowSnr) && (subregion.isHorizontal)) isThereBed = true;
            if ((i !== "0") && (subregion.isLowSnr) && (subregion.isHorizontal)) isThereWrongBed = true;
            if (subregion.isDoor) isThereDoor = true;
            // console.log(i, isThereDoor, isThereBed, isThereWrongBed)
        }
        // type12 ma nessun letto
        const alarmType12AndBed = enableOutOfBed && !isThereBed;
        // letto in più
        const alarmWrongBed = isThereBed && isThereWrongBed;
        // letto non in zero
        const alarmWrongSubregionBed = !isThereBed && isThereWrongBed;
        // porte ma senza type13 attivo
        const alarmDoorEvent = !enableDoorEvents && isThereDoor;
        // type13 senza porte
        const alarmDoorSubregions = enableDoorEvents && !isThereDoor;

        let ConfigSanity = 0;
        let alert = "";
        const sanityCheckConifg = !alarmType12AndBed && !alarmWrongBed && !alarmWrongSubregionBed && !alarmDoorEvent && !alarmDoorSubregions;
        if (!sanityCheckConifg) {
            let alertMessages = [
                alarmType12AndBed ? sanityMessages[0] : null,
                alarmWrongBed ? sanityMessages[1] : null,
                alarmWrongSubregionBed ? sanityMessages[2] : null,
                alarmDoorEvent ? sanityMessages[3] : null,
                alarmDoorSubregions ? sanityMessages[4] : null,
            ];
            alert = alertMessages.filter(Boolean).join("\n");
            const pack = (flags) =>
                flags.reduce((acc, flag, i) => acc | ((flag ? 1 : 0) << i), 0);
            ConfigSanity = pack([alarmType12AndBed, alarmWrongBed, alarmWrongSubregionBed, alarmDoorEvent, alarmDoorSubregions]);
        }
        return {sanityCheckConifg, ConfigSanity, alert};
    }

    function toBools(n, length = Math.max(1, Math.floor(Math.log2((n >>> 0) || 1)) + 1)) {
        const u = n >>> 0; // force unsigned 32-bit
        return Array.from({ length }, (_, i) => Boolean((u >>> i) & 1));
    }

    function sanityDB(dbSanity) {
        if (Number(dbSanity) == 0) return;
        const feedbacks = toBools(dbSanity, 5);
        let alertMessages = [
            feedbacks[0] ? sanityMessages[0] : null,
            feedbacks[1] ? sanityMessages[1] : null,
            feedbacks[2] ? sanityMessages[2] : null,
            feedbacks[3] ? sanityMessages[3] : null,
            feedbacks[4] ? sanityMessages[4] : null,
        ];
        const alert = alertMessages.filter(Boolean).join("\n");
        window.alert(alert);
    }

    const salvaModifiche = async () => {
        try {
            backupRef.current = { Region: null, Subregions: null };
            setMod(0);

            const {sanityCheckConifg, ConfigSanity,alert} = sanityCheck();
            if (!sanityCheckConifg) {
                window.alert(alert);
            }

            /*
             * Prepare config payload.
             * Covert selections from number to string!
             */
            const dataConfig = {
                "appConfig": {
                    // device
                    silentMode: silentMode, // boolean
                    ledPolicy: ledPolicySelect.find(opt => opt.value === ledPolicy)?.svalue,
                    volume: volume, // integer
                    // logLevel: logLevelSelect.find(opt => opt.value === logLevel)?.svalue,
                    logLevel: logLevel,
                    learningModeStartTs: learningModeStartTs, // number
                    learningModeEndTs: learningModeEndTs, // number
                    enableTestMode: enableTestMode, // boolean
                    demoMode: demoMode, // boolean
                    offlineMode: offlineMode, // boolean
                    appLogAutoLevel: appLogSelect.find(opt => opt.value === appLogAutoLevel)?.svalue,
                    appLogOnDemandLevel: appLogSelect.find(opt => opt.value === appLogOnDemandLevel)?.svalue,
                    legacyLogFileUpload: legacyLogFileUpload, // boolean - no
                    dryContacts: {
                        primary: {
                            mode: 0,
                            policy: 0
                        },
                        secondary: {
                            mode: 0,
                            policy: 0
                        }
                    },
                    // cadute
                    confirmedToAlertTimeoutSec: confirmedToAlertTimeoutSec, // integer
                    callingDurationSec: callingDurationSec, // integer
                    enableSensitiveMode: enableSensitiveMode, // boolean
                    sensitivityLevel: sensitivityLevel, // float - string
                    thMinEventsForFirstDecision: thMinEventsForFirstDecision, // integer
                    thNumOfDetectionsInChain: thNumOfDetectionsInChain, // integer
                    // presenza
                    presenceReportMinRateMills: presenceReportMinRateMills, // integer - string
                    enablePresencePeriodicReport: enablePresencePeriodicReport, // boolean - no
                    enableDoorEvents: enableDoorEvents, // boolean
                    enableOutOfBed: enableOutOfBed, // boolean
                    suspendDuration_sec: suspendDuration_sec, // integer
                    enableTelemetriesOnEventDuringSuspend: enableTelemetriesOnEventDuringSuspend, // boolean
                    // telemetry
                    telemAlwaysOn: telemAlwaysOn, // boolean - no
                    telemOnBedExit: telemOnBedExit, // boolean - no
                    telemOnFall: telemOnFall, // boolean - no
                    telemOnSensitiveFall: telemOnSensitiveFall, // boolean - no
                    telemOnDoorEvent: telemOnDoorEvent, // boolean - no
                    telemOnOutOfBed: telemOnOutOfBed, // boolean - no
                    // altro
                    dspRecordsPublishPolicy: dspRecordsPublishPolicy, // boolean
                    dspRecordsPublishMaxLatency_sec: dspRecordsPublishMaxLatency_sec, // integer - no
                    enableAnalytics: enableAnalytics, // boolean
                    enableBeaconScanner: enableBeaconScanner, // boolean
                    //bleBeaconMacs: bleBeaconMacs, // array
                    bleBeaconMacs: [],
                    bleBeaconRssiThreshold: bleBeaconRssiThreshold, // integer - string
                    bleServerType: bleServerTypeSelect.find(opt => opt.value === bleServerType)?.svalue,
                    bleCustomDeviceName: bleCustomDeviceName, // string - no
                    enableRssiMonitor: enableRssiMonitor, // boolean
                    rssiThresholdRssiMonitor: rssiThresholdRssiMonitor, // integer
                    samplesNumRssiMonitor: samplesNumRssiMonitor, // integer
                    enableWifiHealthMonitor: enableWifiHealthMonitor, // boolean - no
                    maxDisconnetionDurationSecWifiHealthMonitor: maxDisconnetionDurationSecWifiHealthMonitor, // boolean - no
                    disconnectionsBurstLimitWifiHealthMonitor: disconnectionsBurstLimitWifiHealthMonitor, //integer - no
                    maxDisconnectionsPerHourAverageWifiHealthMonitor: maxDisconnectionsPerHourAverageWifiHealthMonitor, // integer - no
                    ntpPrimaryBackupServer: ntpPrimaryBackupServer, // string - no
                    ntpSecondaryBackupServer: ntpSecondaryBackupServer, // string - no
                    dryContactActivationDuration_sec: dryContactActivationDuration_sec, // integer
                    // deprecated
                    // ledMode
                    // telemetryPolicy
                    //telemetryTransport
                    // trackerTargetsDebugPolicy
                },
                "walabotConfig": {
                    // device
                    //sensorMounting: sensorMountingSelect.find(opt => opt.value === sensorMounting)?.svalue,
                    sensorMounting: sensorMounting,
                    algoProfile: algoProfileSelect.find(opt => opt.value === algoProfile)?.svalue,
                    xMin: Region.xMin,
                    xMax: Region.xMax,
                    yMin: Region.yMin,
                    yMax: Region.yMax,
                    zMin: Region.zMin,
                    zMax: Region.zMax,
                    trackerSubRegions: Subregions,
                    sensorHeight: Region.sensorHeight,
                    enterDuration: Region.enterDuration,
                    exitDuration: Region.exitDuration,
                    // cadute
                    fallingMitigatorEnabled: fallingMitigatorEnabled, // boolean
                    fallingMitigatorThreshold: fallingMitigatorThreshold, // number
                    // fallingSensitivity: fallingSensitivitySelect.find(opt => opt.value === fallingSensitivity)?.svalue,
                    fallingSensitivity: fallingSensitivity,
                    durationUntilConfirm_sec: durationUntilConfirm_sec, // float (?)
                    minTimeOfTarInFallLoc_sec: minTimeOfTarInFallLoc_sec, // float (?)
                    // presenza
                    bedExitNFramesToReset: bedExitNFramesToReset, // integer
                    expandRegInMeters: expandRegInMeters, // number
                    zThresholdMeters: zThresholdMeters, // number
                    // telemetry
                    enableBedExitTelemetry: enableBedExitTelemetry, // boolean
                    enableBedExitStateTelemetry: enableBedExitStateTelemetry, // boolean
                    enableTrackerTargetTelemetry: enableTrackerTargetTelemetry, // boolean
                    enableDoorEventTelemetry: enableDoorEventTelemetry, // boolean
                    enablePeakTelemetry: enablePeakTelemetry, // boolean
                    enableAboveThPointTelemetry: enableAboveThPointTelemetry, // boolean
                    enableIslandPointTelemetry: enableIslandPointTelemetry, // boolean
                    enableHeightProfileTelemetry: enableHeightProfileTelemetry, // boolean
                    enableOtfPointTelemetry: enableOtfPointTelemetry, // boolean
                    enableFallingTelemetry: enableFallingTelemetry, // boolean
                    enableSensitiveFallingTelemetry: enableSensitiveFallingTelemetry, // boolean
                    enablePresenceTelemetry: enablePresenceTelemetry, // boolean
                    enableImageParamsTelemetry: enableImageParamsTelemetry, // boolean
                    enableInterfererLocHistoryTelemetry: enableInterfererLocHistoryTelemetry, // boolean
                    enableMtiParamsTelemetry: enableMtiParamsTelemetry, // boolean
                    enableReferenceTelemetry: enableReferenceTelemetry, // boolean
                    enableSuiteTelemetry: enableSuiteTelemetry, // boolean
                    enableClustersTelemetry: enableClustersTelemetry, // boolean
                    enableSubRegionStateTelemetry: enableSubRegionStateTelemetry, // boolean
                    // altro
                    performAgc: performAgc, // boolean
                    performHeatup: performHeatup, // boolean
                    // deprecated
                    // maxTargetsForFallingTrigger
                    // bedExitEnabled
                    // bedExitPredictionThreshold
                    // dryContactActivationDuration_sec
                },
                "rfProfile": {
                    rfRegulationZone: rfRegulationZoneSelect.find(opt => opt.value === rfRegulationZone)?.svalue,
                    rfBandWidth: rfBandWidthSelect.find(opt => opt.value === rfBandWidth)?.svalue,
                },
                productType: productTypeSelect.find(opt => opt.value === productType)?.svalue,
            }

            /*
             * Use config payload to create a payload for DB saving.
             * The entries that in configuration were sent as string, they need to be "converted back" to numbers.
             */
            const dataDB = {
                "deviceInfo": {
                    "deviceId": props.sensore.deviceInfo.deviceId,
                    "Struttura": struttura === undefined ? props.sensore.deviceInfo.Struttura : struttura,
                    "Stanza": stanza === undefined ? props.sensore.deviceInfo.Stanza : stanza,
                    "Piano": piano === undefined ? props.sensore.deviceInfo.Piano : piano,
                    "Tipo": tipoStanza === undefined ? props.sensore.deviceInfo.Tipo : tipoStanza,
                    "Commenti": commenti === undefined ? props.sensore.deviceInfo.Commenti : commenti,
                    "ConfigVersion": CONFIGURATOR_VERSION,
                    "ConfigSanity": ConfigSanity,
                    "ConfigDate": new Date().toISOString(),
                    "Config": JSON.parse(JSON.stringify(dataConfig)) // deep clone
                }
            }
            dataDB.deviceInfo.Config.appConfig.ledPolicy = ledPolicy;
            dataDB.deviceInfo.Config.appConfig.logLevel = logLevel;
            dataDB.deviceInfo.Config.appConfig.appLogAutoLevel = appLogAutoLevel;
            dataDB.deviceInfo.Config.appConfig.appLogOnDemandLevel = appLogOnDemandLevel;
            dataDB.deviceInfo.Config.appConfig.bleServerType = bleServerType;
            dataDB.deviceInfo.Config.walabotConfig.sensorMounting = sensorMounting;
            dataDB.deviceInfo.Config.walabotConfig.fallingSensitivity = fallingSensitivity;
            dataDB.deviceInfo.Config.walabotConfig.algoProfile = algoProfile;
            dataDB.deviceInfo.Config.rfProfile.rfRegulationZone = rfRegulationZone;
            dataDB.deviceInfo.Config.rfProfile.rfBandWidth = rfBandWidth;
            dataDB.deviceInfo.Config.productType = productType;

            const response = await sensor.editSensorConfig(token, props.sensore.deviceInfo.deviceId, dataDB);
            
            if (response) {
                mqttclient.publish('/devices/' + props.sensore.deviceInfo.deviceId + '/config', JSON.stringify(dataConfig), 0, true);
            } else if (response === "unauthorized") {
                console.log("Sessione scaduta");
                props.login()
            } else if (response === "notfound") {
                console.log("Sensore non trovato");
            } else {
                console.log("Errore");
            }

            if (props.logging !== logging) {
                const log_res = await LoggingApi.setLogging(token, props.sensore.deviceInfo.deviceId);
                // console.log("api for logging:", log_res);
            }

            props.closePopup();
        } catch (error) {
            console.log(error);
        }

        //window.location.reload();
    }

    const rebootDevice = () => {
        //reboot the device
        mqttclient.publish('/devices/' + props.sensore.deviceInfo.deviceId + '/commands', JSON.stringify({ type: "Reboot" }));
        mqttDisconnect();
        props.closePopup();
    }

    const [receivedTargets, setReceivedTargets] = useState([]);
    const [regionBooleans, setRegionBooleans] = useState(Array(6).fill(false));
    const [presence, setPresence] = useState(false);
    const [latestType4, setLatestType4] = useState("-");

    const containerRef = useRef(null);
    const [pixelWidth, setPixelWidth] = useState(0);
    const [pixelHeight, setPixelHeight] = useState(0);
    const [bedroomlayoutCenterX, setBedroomlayoutCenterX] = useState(null);
    const [commenti, setCommenti] = useState(props.sensore.deviceInfo.Commenti);

    useEffect(() => {
        if (!containerRef.current || !alertInput) return;

        const observer = new ResizeObserver(entries => {
            for (let entry of entries) {
                const { width, height } = entry.contentRect;
                setPixelWidth(width);
                setPixelHeight(height);
            }
            const containerRect = containerRef.current.getBoundingClientRect();
            const centerX = containerRect.left + containerRect.width / 2;
            setBedroomlayoutCenterX(centerX);
        });

        observer.observe(containerRef.current);

        // Cleanup when component unmounts
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (status === 1 && containerRef.current && !alertInput) {
            const { offsetWidth, offsetHeight } = containerRef.current;
            if (offsetWidth > 0 && offsetHeight > 0) {
                setPixelWidth(offsetWidth);
                setPixelHeight(offsetHeight);
            }
            const containerRect = containerRef.current.getBoundingClientRect();
            const centerX = containerRect.left + containerRect.width / 2;
            setBedroomlayoutCenterX(centerX);
        }
    }, [status]);

    const getMinMaxSensor = (mounting, min, max) => {
        if (mounting === 0) return getMinMaxSensorWall(min, max);
        if (mounting === 2) return getMinMaxSensor45Ceiling(min, max);
    }

    let gridLines = [];
    const generateGridLines = (width, height) => {
        let lines = [];
        
        if (gridLines && ((gridLines.length > 0) || alertInput)) {
            return gridLines;
        }
        if ((width == 0) || (height == 0)) {
            console.log("wrong size: ", width, height)
            return lines;
        }

        const step = 0.5 * (pixelWidth / 4); // 4 meters (Vayyar sensor width)
        let min = 0;
        let max = 0;
        if (sensorMounting === 2) {
            min = Region.yMin;
            max = Region.yMax;
            const sensorRange = getMinMaxSensor(2, min, max);

            if (!sensorRange.result) return lines;
            const sens_yMax = sensorRange.sensorMax;
            const yTop = (sens_yMax - (sens_yMax % 0.5)).toFixed(1);
            const marginTop = (sens_yMax % 0.5).toFixed(1) * (pixelWidth / 4);

            lines = generateGridLines45Ceiling(width, height, step, yTop, marginTop);
        } else {
            min = Region.xMin;
            max = Region.xMax;
            const sensorRange = getMinMaxSensor(0, min, max);

            if (!sensorRange.result) return lines;
            const sens_xMax = sensorRange.sensorMax;
            const xLeft = (sens_xMax - (sens_xMax % 0.5)).toFixed(1);
            const marginLeft = (sens_xMax % 0.5).toFixed(1) * (pixelWidth / 4);

            lines = generateGridLinesWall(width, height, step, xLeft, marginLeft);
        }
        
        gridLines = lines;
        return lines;
    };

    return (
        <>
            <SensorWebSocket
                sensorId={props.sensore.deviceInfo.deviceId}
                onMessages={() => {setLogNData(prev => prev + 1);}}
                
            />
            <PopupHeaderTabs
                status={status}
                setStatus={setStatus}
                selSelettore={selSelettore}
                defSelettore={defSelettore}
            />

            <div id="PopupBody2">
                

            {/* TAB INFO */}
            {status === 0 ? <>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <SensorConfigTable
                        mod={mod}
                        struttura={struttura}
                        ListaStrutture={ListaStrutture}
                        setStruttura={setStruttura}
                        stanza={stanza}
                        setStanza={setStanza}
                        piano={piano}
                        ListaPiani={ListaPiani}
                        setPiano={setPiano}
                        getlistaPiani={getlistaPiani}
                        tipoStanza={tipoStanza}
                        TipoStanza={TipoStanza}
                        setTipoStanza={setTipoStanza}
                        rebootDevice={rebootDevice}
                        mac={props.mac}
                        rssi={props.rssi}
                        serial={props.serial}
                        sensore={props.sensore}
                        logging={logging}
                        setLogging={setLogging}
                    />
                    <LearningPeriodWidget
                        learningStartTime={learningModeStartTs}
                        learningEndTime={learningModeEndTs}
                        setLearningStartTime={setLearningModeStartTs}
                        setLearningEndTime={setLearningModeEndTs}
                    />
                    
                    <div><br/>Versione Configurazione: {configVersion}</div>
                    <div>
                        <table class="compact">
                            <thead>
                                <tr>
                                    <th scope="col">Richiesta</th>
                                    <th scope="col">Errore</th>
                                    <th scope="col">Feedback</th>
                                    <th scope="col">Conformità</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>
                                        {isConfigVersionOk ? <span style={{ color: 'green', fontWeight: 'bold' }}>OK</span>
                                                           : <span style={{ color: 'red', fontWeight: 'bold' }}>{CONFIGURATOR_VERSION}</span>
                                        }
                                    </td>
                                    <td>
                                        {isConfigErrorAvailable ? isConfigErrorOK
                                                                  ? <span style={{ color: 'green', fontWeight: 'bold' }}>OK</span>
                                                                  : <span style={{ color: 'red', fontWeight: 'bold' }}>X</span>
                                                                : <span style={{ color: 'black', fontWeight: 'bold' }}>-</span>
                                        }
                                    </td>
                                    <td>
                                        {isConfigFeedbackAvailable ? isConfigFeedbackOK
                                                                     ? <span style={{ color: 'green', fontWeight: 'bold' }}>OK</span>
                                                                     : <span style={{ color: 'red', fontWeight: 'bold' }}>X</span>
                                                                   : <span style={{ color: 'black', fontWeight: 'bold' }}>-</span>
                                        }
                                    </td>
                                    <td onClick={() => {sanityDB(configSanity)}}>
                                        {isConfigSanityOK ? <span style={{ color: 'green', fontWeight: 'bold' }}>OK</span>
                                                          : isConfigSanityAvailable
                                                            ? <span style={{ color: 'red', fontWeight: 'bold' }}>X</span>
                                                            : <span style={{ color: 'black', fontWeight: 'bold' }}>-</span>
                                        }
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <SensorSettings
                        title="Presence and events"
                        color="lightblue"
                        mod={mod}
                        settings={PresenceEventsSettings}
                        fieldConfig={PresenceEventsSettingsConfig}
                    />
                    <Commenti 
                        value={commenti}
                        onChange={setCommenti}
                        readonly={mod === 0}
                    />
                    <div style={{marginLeft: "1em"}}><br/>Data Config.: {configDateISO}</div>
                    <div style={{marginLeft: "1em", color: configFeedbackReceived ? "green" : "black"}}><br/>Feedback Config.: {configFeedbackDateISO}</div>
                </div>
                    
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <SensorSettings
                        title="Cadute"
                        color="red"
                        mod={mod}
                        settings={CaduteSettings}
                        fieldConfig={CaduteSettingsConfig}
                    />
                    <SensorSettings
                        title="Cadute (type5)"
                        color="red"
                        mod={mod}
                        settings={CaduteT5Settings}
                        fieldConfig={CaduteT5SettingsConfig}
                    />
                    <SensorSettings
                        title="Cadute (type8)"
                        color="red"
                        mod={mod}
                        settings={CaduteT8Settings}
                        fieldConfig={CaduteT8SettingsConfig}
                    />
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <SensorSettings
                        title="Sensore"
                        color="orange"
                        mod={mod}
                        settings={SensoreSettings}
                        fieldConfig={SensoreSettingsConfig}
                    />
                    <SensorSettings
                        title="Telemetry"
                        color="blue"
                        mod={mod}
                        settings={TelemetrySettings}
                        fieldConfig={TelemetrySettingsConfig}
                    />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <SensorSettings
                        title="WiFi"
                        color="silver"
                        mod={mod}
                        settings={WiFiSettings}
                        fieldConfig={WiFiSettingsConfig}
                    />
                    <SensorSettings
                        title="BLE"
                        color="brown"
                        mod={mod}
                        settings={BLESettings}
                        fieldConfig={BLESettingsConfig}
                    />
                </div>

            {/* TAB REGION */}
            </> : status === 1 ? <>

                <RegionTabGroup
                    selSelettore={selSelettore}
                    defSelettore={defSelettore}
                    mod={mod}
                    tabs={tabs}
                    setTabs={setTabs}
                    tabConfig={tabConfig}
                    sensore={props.sensore}
                    sensorMounting={sensorMounting}
                    Region={Region}
                    renderInputXYZWithError={renderInputXYZWithError}
                    renderInputWithError={renderInputWithError}
                    Subregions={Subregions}
                    renderSubregionInputWithError={renderSubregionInputWithError}
                    setSubregion={setSubregion}
                    setSubregionName={setSubregionName}
                    addSubregion={addSubregion}
                    removeSubregion={removeSubregion}
                />

                <div style={{ position: 'relative', width: '2em' }}>
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%) rotate(-90deg)',
                            transformOrigin: 'center',
                            whiteSpace: 'nowrap',
                            color: 'blue',
                        }}
                    >
                        {sensorMounting === 2 ? 'X-MIN' : 'X-MAX'}
                    </div>
                </div>

                <div className="bedroom-edit-container">
                    <div className="bedroom-wrapper" ref={containerRef}>
                        <BedroomLayout
                            mod={mod}
                            active={status === 1}
                            pixelWidth={pixelWidth}
                            pixelHeight={pixelHeight}
                            sensorMounting={sensorMounting}
                            Region={lastValidRegion}
                            Subregions={Subregions}
                            setSubregions={setSubregions}
                            getMinMaxSensor={getMinMaxSensor}
                            target={receivedTargets}
                            regionBooleans={regionBooleans}
                            falls={falls}
                        />

                        {(pixelWidth > 0) && (pixelHeight > 0) && !alertInput && (
                            <svg
                                width={pixelWidth}
                                height={pixelHeight}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    zIndex: 0,
                                    pointerEvents: 'none',
                                }}
                            >
                                {generateGridLines(pixelWidth, pixelHeight)}
                            </svg>
                        )}
                    </div>
                </div>

                <div>
                    <h3>Height Legend</h3>
                    <HeightLegend />
                    {(sensorMounting === 2) 
                    ? <img src={ceiling_mounting} width="200" height="200" alt="Legend" />
                    : <img src={wall_mounting} width="200" height="200" alt="Legend" />
                    }
                    <TargetsList receivedTargets={receivedTargets} />
                </div>

                <Log
                    getLoggingDataRange={getLoggingDataRange}
                    eraseLogging={eraseLogging}
                    NData={logNData}
                    page={logPage}
                    setPage={setLogPage}
                    nomeFile={props.sensore.deviceInfo.Piano+"-"+props.sensore.deviceInfo.Stanza+"-"+props.sensore.deviceInfo.Struttura}
                    falls={falls}
                    setFalls={setFalls}
                />

            {/* TAB CONF2 */}
            </> : status === 2 ? <>

                <SensorSettings
                    title="Sensore - extra"
                    color="green"
                    mod={mod}
                    settings={SensoreExtraSettings}
                    fieldConfig={SensoreExtraSettingsConfig}
                />

                <SensorSettings
                    title="Other"
                    color="yellow"
                    mod={mod}
                    settings={OtherSettings}
                    fieldConfig={OtherSettingsConfig}
                />

                <div style={{ maxHeight: 'auto', overflowY: 'auto' }}>
                    <SensorSettings
                        title="Vayyar Telemetry"
                        color="blue"
                        mod={mod}
                        settings={TelemetryVayyarSettings}
                        fieldConfig={TelemetryVayyarSettingsConfig}
                    />
                </div>

            {/* TAB TEST */}
            </> : status === 3 ? <>

                <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", justifyContent: "left", height: "50px" }}>
                    <button style={{ marginRight: "10px" }} onClick={() => testFall("calling", props.sensore.deviceInfo.deviceId, mqttclient)}>Testa Caduta (type5)</button>
                    <button style={{ marginRight: "10px" }} onClick={() => testFall("finished", props.sensore.deviceInfo.deviceId, mqttclient)}>Fine Caduta (type5)</button>
                    <button style={{ marginRight: "10px" }} onClick={() => testSensitiveFall("calling", props.sensore.deviceInfo.deviceId, mqttclient)}>Testa Caduta (type8)</button>
                    <button style={{ marginRight: "10px" }} onClick={() => testSensitiveFall("finished", props.sensore.deviceInfo.deviceId, mqttclient)}>Fine Caduta (type8)</button>
                    <div style={{ flexBasis: "100%", height: 10 }} />
                    <button style={{ marginRight: "10px" }} onClick={() => testPresence(true, props.sensore.deviceInfo.deviceId, mqttclient)}>Entrata Stanza</button>
                    <button style={{ marginRight: "10px" }} onClick={() => testPresence(false, props.sensore.deviceInfo.deviceId, mqttclient)}>Uscita Stanza</button>
                    <button style={{ marginRight: "10px" }} onClick={() => testBed(1, props.sensore.deviceInfo.deviceId, mqttclient)}>Entrata Letto</button>
                    <button style={{ marginRight: "10px" }} onClick={() => testBed(0, props.sensore.deviceInfo.deviceId, mqttclient)}>Uscita Letto</button>
                    <div style={{ flexBasis: "100%", height: 10 }} />
                    <button style={{ marginRight: "10px" }} onClick={() => testType12(props.sensore.deviceInfo.deviceId, mqttclient)}>Out of Bed</button>
                    <div style={{ flexBasis: "100%", height: 10 }} />
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <label for="number">door:</label>
                        <select id="doorSelected" value={selectedDoor} onChange={e => setSelectedDoor(Number(e.target.value))}>
                            {doorSubregions.map(door => (
                                <option key={door.id} value={door.id}>{door.name}</option>
                            ))}
                        </select>
                        <div style={{ marginRight: "20px" }}>{selectedDoor}</div>
                    </div>
                    <button style={{ marginRight: "10px" }} onClick={() => testType13(props.sensore.deviceInfo.deviceId, mqttclient, selectedDoor, "Enter")}>
                        Enter
                    </button>
                    <button style={{ marginRight: "10px" }} onClick={() => testType13(props.sensore.deviceInfo.deviceId, mqttclient, selectedDoor, "Exit")}>
                        Exit
                    </button>
                </div>

            {/* TAB VAYYAR DOC */}
            </> : status === 4 ? <>
                <HtmlTableDocConfig42 />
            </> : <></>}
            </div>

            <PopupBottomButtons 
                mod={mod} 
                setMod={setMod}
                status={status}
                alertInput={alertInput}
                salvaModifiche={salvaModifiche}
                bedroomlayoutCenterX={bedroomlayoutCenterX}
                latestType4={latestType4}
                presence={presence}
                sensorMounting={sensorMounting}
            />
        </>
    );
}

export default SchedaSensore;
